import type { PublicRecord } from '@/types/publicRecords';
import { isGeospatialVisible } from '@/lib/geospatialRecords';
import {
  hasProofSummary,
  isCertificateIssued,
  isCommunitySummaryVisible,
  isProofSummaryEligible,
} from '@/lib/recordTrust';
import { shouldUseDemoData } from '@/lib/demo-content';

export const PUBLIC_RECORDS: PublicRecord[] = [
  {
    id: 'rec-001',
    title: 'Built Ertzyx Ledger as a Solo Founder',
    description:
      'Designed, developed, and launched the Ertzyx dual-ledger platform as a solo founder — from initial architecture through beta, covering product design, full-stack engineering, institutional partnerships, and investor outreach.',
    recordType: 'individual_achievement',
    ownerType: 'individual',
    ownerName: 'Bertalia',
    ownerSubtitle: 'Founder, Ertzyx',
    pillar: 'Achievement',
    verificationStatus: 'Peer Reviewed',
    visibility: 'community_summary',
    externalSharingEnabled: true,
    communitySummaryEnabled: true,
    date: '2024-11-15',
    year: 2024,
    locationName: 'Vancouver, Canada',
    latitude: 49.2827,
    longitude: -123.1207,
    evidenceCount: 4,
    evidenceStatus: 'added',
    witnessStatus: 'pending',
    proofCardId: 'EPP-2024-001',
    allowCertificateIssuance: false,
    certificateShareEnabled: false,
    claimType: 'achievement',
    tags: ['founder', 'solo', 'full-stack', 'ledger', 'platform'],
    isArchived: false,
    isFeatured: true,
    showOnGeospatialMap: true,
    approvedForPublicMap: true,
  },
  {
    id: 'rec-002',
    title: 'First Web Summit Alpha Booth',
    description:
      'Represented Ertzyx at the Web Summit Alpha stage — pitching the dual-ledger concept to investors, journalists, and institutional partners. First public demo of the platform outside the founding team.',
    recordType: 'individual_achievement',
    ownerType: 'individual',
    ownerName: 'Bertalia',
    ownerSubtitle: 'Founder, Ertzyx',
    pillar: 'Achievement',
    verificationStatus: 'Witness Confirmed',
    visibility: 'verified_public_proof',
    externalSharingEnabled: true,
    communitySummaryEnabled: true,
    date: '2024-11-11',
    year: 2024,
    locationName: 'Vancouver Convention Centre',
    latitude: 49.2897,
    longitude: -123.1117,
    evidenceCount: 6,
    evidenceStatus: 'added',
    witnessStatus: 'confirmed',
    proofCardId: 'EPP-2024-002',
    certificateId: 'ERT-CERT-2024-002',
    certificateIssued: true,
    certificateEligible: true,
    certificateShareEnabled: true,
    allowCertificateIssuance: true,
    claimType: 'achievement',
    tags: ['web summit', 'pitch', 'alpha', 'investor', 'demo'],
    isArchived: false,
    isFeatured: true,
    showOnGeospatialMap: true,
    approvedForPublicMap: true,
  },
  {
    id: 'rec-003',
    title: 'Journey of Building a Human Ledger',
    description:
      'A structured account of the founding journey — the research, product decisions, failures, pivots, and milestones that shaped the Ertzyx Ledger into what it is today. A living record of the build.',
    recordType: 'individual_legacy',
    ownerType: 'individual',
    ownerName: 'Bertalia',
    ownerSubtitle: 'Founder, Ertzyx',
    pillar: 'Skill / Legacy',
    verificationStatus: 'Public Record',
    visibility: 'community_summary',
    externalSharingEnabled: true,
    communitySummaryEnabled: true,
    date: '2024-12-01',
    year: 2024,
    locationName: 'Vancouver, Canada',
    latitude: 49.2827,
    longitude: -123.1207,
    evidenceCount: 8,
    evidenceStatus: 'added',
    proofCardId: 'EPP-2024-003',
    allowCertificateIssuance: false,
    certificateShareEnabled: false,
    claimType: 'community_story',
    tags: ['legacy', 'founding story', 'build log', 'product', 'ertzyx'],
    isArchived: false,
    isFeatured: false,
    showOnGeospatialMap: true,
    approvedForPublicMap: true,
  },
  {
    id: 'rec-004',
    title: 'Community Research: Oral History Preservation Gaps',
    description:
      'A field research project documenting how communities in the Pacific Northwest preserve — or fail to preserve — oral histories, personal records, and cultural memory across generations.',
    recordType: 'individual_skill',
    ownerType: 'individual',
    ownerName: 'Bertalia',
    ownerSubtitle: 'Founder, Ertzyx',
    pillar: 'Performance',
    verificationStatus: 'Peer Reviewed',
    visibility: 'private',
    externalSharingEnabled: false,
    communitySummaryEnabled: false,
    date: '2024-08-20',
    year: 2024,
    locationName: 'Vancouver, Canada',
    latitude: 49.2827,
    longitude: -123.1207,
    evidenceCount: 5,
    evidenceStatus: 'added',
    proofCardId: 'EPP-2024-004',
    allowCertificateIssuance: false,
    certificateShareEnabled: false,
    claimType: 'other',
    tags: ['research', 'oral history', 'preservation', 'community', 'pacific northwest'],
    isArchived: false,
  },

  {
    id: 'rec-005',
    title: 'Community Oral History Collection',
    description:
      'A curated archive of 42 oral history recordings, photographs, and transcriptions collected from community elders and neighbourhood historians across greater Vancouver. Each record has been reviewed, dated, and contextualised by heritage researchers.',
    recordType: 'heritage_archive',
    ownerType: 'institution',
    ownerName: 'Vancouver Heritage Lab',
    ownerSubtitle: 'Heritage Research Institution',
    pillar: 'History',
    verificationStatus: 'Institution Verified',
    visibility: 'institution_public',
    externalSharingEnabled: true,
    communitySummaryEnabled: true,
    date: '2024-09-30',
    year: 2024,
    locationName: 'Vancouver, Canada',
    latitude: 49.2782,
    longitude: -123.1157,
    evidenceCount: 42,
    evidenceStatus: 'reviewed',
    institutionReviewStatus: 'approved',
    proofCardId: 'EPP-INST-2024-001',
    certificateId: 'ERT-CERT-INST-2024-001',
    certificateIssued: true,
    certificateEligible: true,
    certificateShareEnabled: false,
    allowCertificateIssuance: true,
    claimType: 'heritage',
    tags: ['oral history', 'community', 'archive', 'elders', 'heritage'],
    isArchived: false,
    isFeatured: true,
    showOnGeospatialMap: true,
    approvedForPublicMap: true,
  },
  {
    id: 'rec-006',
    title: 'Alumni Achievement Proof Series',
    description:
      'A verified ledger of 128 graduate achievement records from the graduating class, including academic distinctions, research outputs, and community impact records. Each proof card has been reviewed and issued by the registrar.',
    recordType: 'institution_certificate',
    ownerType: 'institution',
    ownerName: 'Pacific Digital College',
    ownerSubtitle: 'Post-Secondary Institution',
    pillar: 'Achievement',
    verificationStatus: 'Gold Verified',
    visibility: 'restricted_proof',
    externalSharingEnabled: true,
    communitySummaryEnabled: true,
    date: '2024-06-15',
    year: 2024,
    locationName: 'British Columbia, Canada',
    latitude: 49.2488,
    longitude: -122.9805,
    evidenceCount: 128,
    evidenceStatus: 'reviewed',
    institutionReviewStatus: 'approved',
    proofCardId: 'EPP-INST-2024-002',
    certificateId: 'ERT-CERT-INST-2024-002',
    certificateIssued: true,
    certificateEligible: true,
    certificateShareEnabled: true,
    allowCertificateIssuance: true,
    claimType: 'institutional_recognition',
    tags: ['alumni', 'graduation', 'certificate', 'academic', 'verified'],
    isArchived: false,
    isFeatured: false,
  },
  {
    id: 'rec-007',
    title: 'Cultural Memory Mapping Project',
    description:
      'A geographic and archival documentation of 76 culturally significant sites, stories, and heritage objects across coastal British Columbia. Produced in partnership with local First Nations communities and heritage conservators.',
    recordType: 'cultural_collection',
    ownerType: 'institution',
    ownerName: 'Coastal Heritage Archive',
    ownerSubtitle: 'Cultural Heritage Organisation',
    pillar: 'History',
    verificationStatus: 'Archived',
    visibility: 'private',
    externalSharingEnabled: false,
    communitySummaryEnabled: false,
    date: '2023-11-01',
    year: 2023,
    locationName: 'British Columbia, Canada',
    latitude: 49.6488,
    longitude: -124.9805,
    evidenceCount: 76,
    evidenceStatus: 'added',
    allowCertificateIssuance: false,
    certificateShareEnabled: false,
    claimType: 'heritage',
    tags: ['cultural mapping', 'first nations', 'heritage sites', 'coastal', 'conservation'],
    isArchived: true,
  },
  {
    id: 'rec-008',
    title: 'Public Volunteer Impact Ledger',
    description:
      'A structured record of 33 verified volunteer impact reports submitted by community members, reviewed by field coordinators, and archived with supporting evidence for civic accountability and grant reporting.',
    recordType: 'institution_verified_record',
    ownerType: 'institution',
    ownerName: 'Civic Service Network',
    ownerSubtitle: 'Non-Profit Organisation',
    pillar: 'Hero',
    verificationStatus: 'Institution Verified',
    visibility: 'community_public',
    externalSharingEnabled: true,
    communitySummaryEnabled: true,
    date: '2024-10-01',
    year: 2024,
    locationName: 'Vancouver, Canada',
    latitude: 49.2648,
    longitude: -123.1857,
    evidenceCount: 33,
    evidenceStatus: 'reviewed',
    institutionReviewStatus: 'approved',
    witnessStatus: 'confirmed',
    proofCardId: 'EPP-INST-2024-003',
    certificateId: 'ERT-CERT-INST-2024-003',
    certificateIssued: true,
    certificateEligible: true,
    certificateShareEnabled: false,
    allowCertificateIssuance: true,
    claimType: 'public_service',
    tags: ['volunteer', 'civic', 'impact', 'community service', 'non-profit'],
    isArchived: false,
    isFeatured: false,
    showOnGeospatialMap: true,
    approvedForPublicMap: true,
  },
  {
    id: 'rec-009',
    title: 'Annual Leadership Recognition Programme',
    description:
      'Institutional recognition records for 18 community leaders who demonstrated exceptional contribution to civic life, education, and cultural preservation across the 2023 calendar year.',
    recordType: 'institution_certificate',
    ownerType: 'institution',
    ownerName: 'Pacific Digital College',
    ownerSubtitle: 'Post-Secondary Institution',
    pillar: 'Hero',
    verificationStatus: 'Gold Verified',
    visibility: 'verified_public_proof',
    externalSharingEnabled: true,
    communitySummaryEnabled: true,
    date: '2024-02-14',
    year: 2024,
    locationName: 'British Columbia, Canada',
    latitude: 49.2488,
    longitude: -122.9805,
    evidenceCount: 18,
    evidenceStatus: 'reviewed',
    institutionReviewStatus: 'approved',
    proofCardId: 'EPP-INST-2024-004',
    certificateId: 'ERT-CERT-INST-2024-004',
    certificateIssued: true,
    certificateEligible: true,
    certificateShareEnabled: true,
    allowCertificateIssuance: true,
    claimType: 'institutional_recognition',
    tags: ['leadership', 'recognition', 'annual', 'civic', 'education'],
    isArchived: false,
    isFeatured: true,
    showOnGeospatialMap: true,
    approvedForPublicMap: true,
  },
  {
    id: 'rec-demo-supplier',
    title: 'Test Supplier Status Credential',
    description:
      'Pre-commercial demonstration record. Supplier-style credential issued by Designated Test Issuer to Designated Test Supplier during the Ertzyx Trust Ledger controlled operational test. No real supplier, procurement, or government data is represented. All values are simulated.',
    recordType: 'institution_certificate',
    ownerType: 'institution',
    ownerName: 'Designated Test Issuer',
    ownerSubtitle: 'Trust Ledger Demo Institution',
    pillar: 'Achievement',
    verificationStatus: 'Institution Verified',
    visibility: 'verified_public_proof',
    externalSharingEnabled: true,
    communitySummaryEnabled: false,
    date: '2026-06-01',
    year: 2026,
    locationName: 'Ottawa, Canada',
    evidenceCount: 3,
    evidenceStatus: 'reviewed',
    witnessStatus: 'confirmed',
    institutionReviewStatus: 'approved',
    proofCardId: 'EPP-DEMO-SUPPLY-001',
    certificateId: 'ERT-CERT-DEMO-SUPPLY-001',
    certificateIssued: true,
    certificateEligible: true,
    certificateShareEnabled: true,
    allowCertificateIssuance: true,
    claimType: 'institutional_recognition',
    tags: ['demo', 'trust-ledger', 'supplier', 'test', 'pre-commercial'],
    isArchived: false,
    isFeatured: false,
    showOnGeospatialMap: false,
    approvedForPublicMap: false,
  },
];

export function getRecordById(id: string): PublicRecord | undefined {
  return PUBLIC_RECORDS.find((r) => r.id === id);
}

export function getPublicRecords(
  ownerType?: 'individual' | 'institution',
  email?: string | null
): PublicRecord[] {
  if (!shouldUseDemoData(email)) return [];
  const visible = PUBLIC_RECORDS.filter(isCommunitySummaryVisible);
  if (ownerType) return visible.filter((r) => r.ownerType === ownerType);
  return visible;
}

export function getFeaturedRecords(email?: string | null): PublicRecord[] {
  if (!shouldUseDemoData(email)) return [];
  return PUBLIC_RECORDS.filter((r) => r.isFeatured && !r.isArchived);
}

export function getGeoRecords(email?: string | null): PublicRecord[] {
  if (!shouldUseDemoData(email)) return [];
  return PUBLIC_RECORDS.filter(isGeospatialVisible);
}

export function getGeospatialMapRecords(options?: {
  ownerType?: 'individual' | 'institution';
  email?: string | null;
}): PublicRecord[] {
  if (!shouldUseDemoData(options?.email)) return [];
  let list = PUBLIC_RECORDS.filter(isGeospatialVisible);
  if (options?.ownerType) {
    list = list.filter((r) => r.ownerType === options.ownerType);
  }
  return list;
}

/** Records with a public proof summary (not certificates). */
export function getProofCards(email?: string | null): PublicRecord[] {
  if (!shouldUseDemoData(email)) return [];
  return PUBLIC_RECORDS.filter((r) => hasProofSummary(r) && isProofSummaryEligible(r));
}

/** Records with an issued certificate only. */
export function getIssuedCertificates(email?: string | null): PublicRecord[] {
  if (!shouldUseDemoData(email)) return [];
  return PUBLIC_RECORDS.filter((r) => isCertificateIssued(r));
}
